import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { WebAppUser, AuthenticationService } from '@services/authentication.service';
import { Subject, combineLatest, takeUntil, distinctUntilChanged, filter, finalize } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account, AccountsService } from '@services/accounts.service';
import { Organisation, OrganisationDataService } from '@services/organisation-data.service';

@Component({
	selector: 'app-routing',
	templateUrl: './routing.component.html',
	styleUrls: ['./routing.component.scss']
})
export class RoutingComponent implements OnInit, OnDestroy {
	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** currentUser */
	public currentUser: WebAppUser;

	/** accounts */
	public myAccounts: Account[] = [];

	/** targetURL */
	public targetURL: string = '';

	/** firstLoad */
	public firstLoad: boolean = true;

	/**
	 * Default Constructor
	 * @param authenticationService authenticationService 
	 * @param siteSubscriptions siteSubscriptions 
	 * @param router router 
	 * @param route route 
	 * @param globalFunctions globalFunctions  
	 */
	constructor(
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		public organisationDataService: OrganisationDataService,
		public accountsService: AccountsService,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions
	) {
		combineLatest([
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.myAccounts$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.fragment.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			// filter(([currentUser, myAccounts, fragment]) => !!currentUser && !!myAccounts && !!fragment)
		).subscribe({
			next: ([currentUser, myAccounts, fragment]) => {
				if (fragment) {
					this.targetURL = decodeURIComponent(fragment || '/');
					if (currentUser && myAccounts) {
						this.currentUser = currentUser;
						this.myAccounts = myAccounts;
						this.myAccounts = this.siteSubscriptions.normalizeFeatures(this.myAccounts) as Account[];

						this.globalFunctions.displaySpinner(true);

						this.resolveRoute();
					} else {
						localStorage.setItem("targetURL", `routing#${this.targetURL}`);
						this.router.navigate([`/`]);
					}
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {
		this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()).subscribe({
			next: (account) => {
				if (account) {
					if (this.firstLoad) {
						this.firstLoad = false;
						return;
					}
					this.globalFunctions.displaySpinner(false);
					this.siteSubscriptions.RefreshMyOrganisations(account.GUID);
					this.router.navigateByUrl(this.targetURL);
				}
			}
		})
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * getOrganisations
	 * @param accountGUID accountGUID  
	 */
	async getOrganisations(accountGUID: string): Promise<Organisation[]> {
		return new Promise<Organisation[]>((resolve, reject) => {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();

			this.organisationDataService.getOrganisations$(accountGUID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((orgs) => !!orgs),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.globalFunctions.displaySpinner(false);
					reject();
				})
			).subscribe({
				next: (orgs: any) => {
					resolve(orgs);
				},
				error: (error) => {
					if (error.status === 404) {

					}
					reject(error);
				}
			});
		});
	}

	/**
	 * resolveRoute
	 */
	async resolveRoute() {
		let targetAccountGUID = this.targetURL.split("/")[1];
		let targetOrganisationUID = this.targetURL.split("/")[3];

		const findAccount = this.myAccounts.find((x: Account) => x.GUID.toLocaleLowerCase() == targetAccountGUID.toLocaleLowerCase());

		if (findAccount) {
			let accountOrgs: Organisation[] = await this.getOrganisations(findAccount.GUID);
			const orgMatch: Organisation | undefined = accountOrgs.find((x: Organisation) => x.Uid.toLocaleLowerCase() == targetOrganisationUID.toLocaleLowerCase());
			if (orgMatch) {
				this.siteSubscriptions.SetSelectedAccount(findAccount);
				this.siteSubscriptions.SetSelectedOrganisation(orgMatch);
			} else {
				this.goToRoute(true, `resolveRoute: Organisation ${targetOrganisationUID} not found for the selected account: ${findAccount.Name}`);
			}
		}
	}

	/**
	 * goToRoute
	 * @param reject reject  
	 */
	goToRoute(reject: boolean = false, message: string = "You do not have permission to access this page") {
		if (!reject) {
		} else {
			this.globalFunctions.displaySpinner(false);
			this.router.navigate(['/unauthorised'], {
				state: {
					url: this.targetURL,
					message: message,
					source: "RoutingComponent"
				}
			});
		}
	}
}

